import type { VersionContextValue } from '@core/context';
import type { $TSFixMe } from '@readme/backend/iso';
import type { ComponentProps } from 'react';

import { useState, useMemo, useEffect } from 'react';

import useClassy from '@core/hooks/useClassy';
import usePrevious from '@core/hooks/usePrevious';
import { useProjectStore, useSuperHubStore } from '@core/store';
import classy from '@core/utils/classy';
import SearchModal from '@Hub/Search/Modal';

import AppSubnav from './components/Subnav';
import AppTopNav from './components/TopNav';
import classes from './style.module.scss';
import './style.scss';

interface AppNavProps {
  isDetachedProductionSite: boolean;
  lang: string;
  search: ComponentProps<typeof SearchModal>;
  version: VersionContextValue;
}

const AppNav = ({ search, isDetachedProductionSite, ...props }: AppNavProps) => {
  const [theme, setTheme] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toggleTargetElement, setToggleTargetElement] = useState<HTMLElement | null>(null);
  const bem = useClassy(classes, 'Header');

  const [
    headerType,
    headerOverlayType,
    headerOverlayImageURL,
    headerOverlayFill,
    headerOverlayPosition,
    navLinkStyle,
    projectLayout,
  ] = useProjectStore(s => [
    s.data.appearance.header.type,
    s.data.appearance.header.overlay.type,
    s.data.appearance.header.overlay.image?.url,
    s.data.appearance.header.overlay.fill,
    s.data.appearance.header.overlay.position,
    s.data.appearance.header.link_style,
    s.data.appearance.layout.style,
  ]);

  const [isSuperHubEditOrReviewMode, isSuperHub] = useSuperHubStore(s => [
    s.isEditing || s.routeAction === 'review',
    s.isSuperHub,
  ]);

  const toggleSearch = (event?: $TSFixMe) => {
    setIsModalOpen(prevIsModalOpen => {
      // If the modal is opening, capture the element to return focus to when it closes
      if (!prevIsModalOpen && event && document.activeElement) {
        setToggleTargetElement(document.activeElement as HTMLElement);
      }

      return !prevIsModalOpen;
    });
  };

  const previousIsModalOpen = usePrevious(isModalOpen);

  useEffect(() => {
    // If modal has just closed
    if (previousIsModalOpen && !isModalOpen) {
      // Return focus to the element that opened the modal
      if (toggleTargetElement) {
        toggleTargetElement.focus();
        setToggleTargetElement(null);
      }
    }
  }, [toggleTargetElement, previousIsModalOpen, isModalOpen]);

  const additionalHeaderClasses = classy(
    headerType === 'gradient' && 'rm-Header_gradient',
    headerType === 'overlay' && `rm-Header_overlay rm-Header_overlay-${headerOverlayType}`,
    headerType === 'overlay' &&
      headerOverlayType === 'custom' &&
      headerOverlayImageURL &&
      `rm-Header_custom rm-Header_custom-pos-${headerOverlayPosition} rm-Header_custom-size-${headerOverlayFill}`,
    headerType === 'line' && navLinkStyle === 'buttons' && 'Header-links-buttons',
  );

  const headerStyling = useMemo(
    () =>
      headerType === 'overlay' && headerOverlayType === 'custom'
        ? { backgroundImage: `url(${headerOverlayImageURL})` }
        : {},
    [headerOverlayImageURL, headerOverlayType, headerType],
  );

  return (
    <header
      className={bem(
        '&',
        `rm-Header_${projectLayout}`,
        isSuperHub && '_collapsible',
        isSuperHubEditOrReviewMode && '_collapsible-hidden',
        'rm-Header',
        additionalHeaderClasses,
      )}
      style={headerStyling}
    >
      <AppTopNav
        isDetachedProductionSite={isDetachedProductionSite}
        search={search}
        setTheme={setTheme}
        toggleSearch={toggleSearch}
        {...props}
      />
      <AppSubnav
        isDetachedProductionSite={isDetachedProductionSite}
        isModalOpen={isModalOpen}
        search={search}
        setTheme={setTheme}
        theme={theme}
        toggleSearch={toggleSearch}
        {...props}
      />

      <SearchModal
        appId={search.appId}
        indexName={search.indexName}
        isModalOpen={isModalOpen}
        projectsMeta={search.projectsMeta}
        searchApiKey={search.searchApiKey}
        toggleOpen={toggleSearch}
        urlManagerOpts={search.urlManagerOpts}
      />
    </header>
  );
};

export default AppNav;
